import { useState, useMemo, useEffect } from "react";
import { PositionData, ClosedPositionData } from "@crypto-trading/common/browser";
import { useChartCandles } from "./useChartCandles";
import {
  CANDLE_INTERVAL_MS,
  CandleFetchErrorMeta,
  CandleInterval,
} from "@/lib/live-candles/candle.types";
import { mapExchangeToSupportedCode } from "@/lib/live-candles/candle-symbols";

export type Interval = CandleInterval;
export const INTERVAL_DURATIONS_MS: Record<Interval, number> = CANDLE_INTERVAL_MS;

/** Intervals shown in the chart interval selector. */
export const CHART_INTERVAL_OPTIONS: Interval[] = ["1m", "15m", "4h"];

export function pickChartInterval(enabledIntervals: Interval[]): Interval {
  return (
    CHART_INTERVAL_OPTIONS.find((interval) => enabledIntervals.includes(interval)) ??
    enabledIntervals[0] ??
    "1m"
  );
}

const MAX_CANDLES = 300;
const PADDING_CANDLES = 20; // 10 before + 10 after

const isClosedPosition = (
  pos: PositionData | ClosedPositionData
): pos is ClosedPositionData => {
  return "closedAt" in pos && pos.closedAt !== null;
};

interface UsePositionPriceDataParams {
  userId: string;
  position: PositionData | ClosedPositionData | null;
  open: boolean;
}

export const usePositionPriceData = ({
  userId,
  position,
  open,
}: UsePositionPriceDataParams) => {
  const closed = position ? isClosedPosition(position) : false;

  // Calculate start time for price data
  const startTime = position?.openedAt ? new Date(position.openedAt).getTime() : null;

  // Calculate which intervals are enabled (for opened positions, all intervals are enabled since we use 25 candles)
  const enabledIntervals = useMemo(() => {
    if (!startTime) return [];

    const intervals: Interval[] = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];

    if (closed && position && "closedAt" in position && position.closedAt) {
      // For closed positions, calculate based on actual period duration
      const closedAtTime = new Date(position.closedAt).getTime();
      const periodDurationMs = closedAtTime - startTime;

      return intervals.filter((interval) => {
        const intervalDurationMs = INTERVAL_DURATIONS_MS[interval];
        const periodCandles = Math.ceil(periodDurationMs / intervalDurationMs);
        const totalCandles = periodCandles + PADDING_CANDLES;
        return totalCandles <= MAX_CANDLES;
      });
    } else {
      // For opened positions, all intervals are enabled (25 candles + padding = 35 < 300)
      return intervals;
    }
  }, [startTime, closed, position]);

  // Set default interval to first enabled interval, or '1m' if none enabled
  const [selectedInterval, setSelectedInterval] = useState<Interval>("1m");

  const positionKey = position
    ? "positionId" in position
      ? position.positionId
      : position.id
    : null;

  // Calculate endTime: closed positions use closedAt; open positions load through now
  const endTime = useMemo(() => {
    if (!startTime) return null;

    if (closed && position && "closedAt" in position && position.closedAt) {
      return new Date(position.closedAt).getTime();
    }

    return Date.now();
  }, [startTime, closed, position]);

  // Sync to a visible chart interval when the drawer opens or the position changes
  useEffect(() => {
    if (!open || !positionKey || enabledIntervals.length === 0) return;
    setSelectedInterval((current) => {
      if (
        CHART_INTERVAL_OPTIONS.includes(current) &&
        enabledIntervals.includes(current)
      ) {
        return current;
      }
      return pickChartInterval(enabledIntervals);
    });
  }, [open, positionKey, enabledIntervals]);

  useEffect(() => {
    if (enabledIntervals.length === 0) return;
    setSelectedInterval((current) => {
      if (enabledIntervals.includes(current)) {
        return current;
      }
      return pickChartInterval(enabledIntervals);
    });
  }, [enabledIntervals]);

  const exchange = useMemo(
    () =>
      mapExchangeToSupportedCode(
        position?.exchangeCode ?? position?.exchangeName ?? null
      ),
    [position?.exchangeCode, position?.exchangeName]
  );

  const {
    priceChanges,
    isLoading,
    isLoadingMore,
    error,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
  } = useChartCandles({
    userId,
    symbol: position?.symbol || "",
    exchange,
    interval: selectedInterval,
    startTime,
    endTime,
    enabled: open && !!userId && !!position && !!startTime && !!endTime,
    paddingCandles: PADDING_CANDLES / 2,
    focusAnchorTime: closed ? null : startTime,
    focusEndTime: closed ? endTime : null,
  });

  return {
    enabledIntervals,
    selectedInterval,
    setSelectedInterval,
    priceChanges,
    isLoading,
    isLoadingMore,
    error: error as CandleFetchErrorMeta | null,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
    endTime,
    startTime,
    exchange,
  };
};

